import { createContext, PropsWithChildren, useContext, FC, useEffect, useCallback, useState } from 'react';
import { UserDto, getUserEmail, getUserPhoneNumber } from "@sweatpals/shared";

export interface GtmContextShape {
	baseEventTrackingData?: {
		customer_email?: string;
		customer_phone?: string;
		customer_id?: string;
		membership_status?: string;
		organizer_id?: string;
		organizer_name?: string;
		organizer_type?: string;
	};
	setUserDataExternaly?: (user?: UserDto, community?: UserDto) => void
	// Provide a safe way to push into the GTM dataLayer from anywhere in the app
	dataLayerPush?: (payload: Record<string, unknown>) => void;
}

const GtmContext = createContext<GtmContextShape>({});

export const useGtmContext = () => useContext(GtmContext);

export type GtmContextProviderProps = PropsWithChildren & {
	currentUser?: UserDto | null;
	currentCommunity?: UserDto | null;
};

/**
 * Firebase context provider for handling analytics and feature flags in the application.
 *
 * The main purpose of this context is to make logging and feature-flags versatile for any part of the app.
 * It provides functionality for initializing Firebase, setting user IDs for analytics, and handling feature flags.
 *
 * @example
 * ```tsx
 * import { FirebaseContextProvider } from './firebase.context';
 *
 * function App() {
 *   const user = getCurrentUser();
 *   const community = getCurrentCommunity();
 *
 *   return (
 *     <FirebaseContextProvider currentUser={user} currentCommunity={community}>
 *       <YourAppContent />
 *     </FirebaseContextProvider>
 *   );
 * }
 * ```
 *
 * @note
 * Note: This context enables the use of hook utilities like use-firebase-analytics
 * anywhere in your component tree of any app. These hooks can be
 * used in any component that's wrapped with this provider.
 */
export const GtmContextProvider: FC<GtmContextProviderProps> = ({ children, currentUser, currentCommunity }) => {
  const [baseEventTrackingData, setBaseEventTrackingData] = useState<{
    customer_email?: string,
	  customer_phone?: string,
	  customer_id?: string,
	  membership_status?: string,
	  organizer_id?: string,
	  organizer_name?: string,
    organizer_type?: string,
  }>({})

	const setUserDataExternaly = useCallback((user?: UserDto, community?: UserDto) => {
		setBaseEventTrackingData({
			...(user 
					? { 
							customer_id: user.id, 
							customer_email: getUserEmail(user), 
							customer_phone: getUserPhoneNumber(user) 
						} 
					: {}
				),
			...(community 
					? { 
							organizer_id: community.id,
							organizer_name: community.fullName,
							organizer_type: community.isProPlanActive ? 'pro' : 'standard'
						} 
					: {}
			),
		})
	}, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).dataLayer = (window as any).dataLayer || [];
    }
  }, [])

  const dataLayerPush = useCallback((payload: Record<string, unknown>) => {
    if (typeof window === 'undefined') return;
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    try {
      w.dataLayer.push(payload);
    } catch (e) {
      console.error('[GTM] Failed to push to dataLayer', e);
    }
  }, [])

  useEffect(
    () => {
      setUserDataExternaly(
				currentUser ?? undefined,
				currentCommunity ?? undefined
			);
    },
    [currentUser, currentCommunity]
  )

	return (
		<GtmContext.Provider value={{ baseEventTrackingData, setUserDataExternaly, dataLayerPush }}>
			{children}
		</GtmContext.Provider>
	);
};
